import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import { Redirect } from 'react-router-dom';

import KioskService from 'app/services/KioskService';
import config from 'shared/config/config';
import juggle from 'shared/utils/juggle';
import TimeOutDialog from 'app/components/TimeOutDialog';

import {
  addMoment,
  getPurchaseCalculations
} from 'shared/actions/cartActions';

import {
  getKioskSettings,
  setKioskLoaded,
  viewCatalog,
  viewGallery,
  getKioskPrintSizes,
  getDefaultStripeReader,
  showTimeOutDialog
} from 'app/actions/kioskActions';

import {
  onAccountMessage
} from 'shared/services/Pusher';

import {
  trackEvent
} from 'shared/services/Analytics';

import {
  selectLocale,
  setUppercaseLabels
} from 'shared/actions/messagesActions';

import {
  getStore
} from 'shared/services/Store';

import {
  setThemeConfig
} from 'shared/config/themes';

import {
  setPhotoSwipeHidden,
} from 'app/actions/photoSwipeActions';

const styles = () => ({
  text: {
    float: 'left',
    textAlign: 'left',
    width: '100%',
    margin: '0 0 0.5rem 0',
    padding: 0,
    fontFamily: config.root_font_family
  }
});

const isViewPhotosItem = localStorage.getItem('VIEW_PHOTOS');
const isViewPhotos = isViewPhotosItem === 'false' || !isViewPhotosItem ? false : true;

class LoadKiosk extends React.Component {
  constructor(props) {
    super(props);

    this.store = getStore();
    this.store.jobs ||= {
      byId: {},
      allIds: []
    }
    this.store.moments ||= {
      byId: {},
      allIds: []
    }
    this.store.jobMoments ||= {};

    let matches = window.location.search.match(/since=(.+)&*/);
    if (matches) {
      this.since = matches[1];
    }

    // track most recent loading job
    this.jobsIndex = 0;
    this.timeOutDisabled = isViewPhotos;

    this.state = {
      loading: !props.loaded,
      kioskNotFound: false,
      logs: ['Loading kiosk settings...'],
      error: null
    }
  }

  componentDidMount() {
    if (this.state.loading) {
      const {
        match: { params: { token } },
        getKioskSettings,
        setKioskLoaded,
        setUppercaseLabels,
        selectLocale,
      } = this.props;

      getKioskSettings(token)
        .then(() => {
          const { theme, uppercase_labels, locales } = this.props.settings;
          if (theme) {
            setThemeConfig(theme);
          }
          if (uppercase_labels) {
            setUppercaseLabels(true);
          }

          // set locale to first enabled locale
          try {
            let locale = locales.find(l => l.enabled);
            selectLocale(locale.locale);
          } catch (err) {
            console.error(err);
          }

          this.updateLog('Loaded kiosk settings.');
          this.appendLog('Loading jobs...');

          return this.getJobs(token);
        })
        .then(() => {
          return this.loadJobs();
        })
        .then(() => {
          const { account_id, token } = this.props.settings;
          this.appendLog('Loading stripe details...');
          this.props.getDefaultStripeReader();
        })
        .then(() => {
          // if catalog screen is enabled and there are no moments,
          // then throw an error


          const { catalog_screen, gallery_screen, account_id, token, kiosk_id } = this.props.settings;
          const { jobMoments } = this.store;

          if (catalog_screen) {
            let momentsCount = Object.keys(jobMoments)
              .map(jobId => jobMoments[jobId])
              .reduce((total, moments) => total += moments.length, 0);

            if (!momentsCount) {
              throw "ERROR: No images. Please upload images for the catalog and verify that the upload was successful.";
            }
          }

          console.log("init websocket");
          onAccountMessage(account_id, "moment", "available", msg => this.onMomentAvailable(msg.moment));


          if (gallery_screen) {
            this.props.getKioskPrintSizes(kiosk_id);
          }

          if (this.props.settings.timeout && !this.timeOutDisabled) {
            this.startInterval();

            window.addEventListener("mousemove", () => this.reset());
            window.addEventListener("scroll", () => this.reset());
            window.addEventListener("keydown", () => this.reset());
          }

          setKioskLoaded();

          this.setState({
            loading: false
          });

          //
        })
        .catch(err => {
          console.error(err);

          this.setState({
            kioskNotFound: !this.props.settings,
            error: typeof err === 'string' ? err : JSON.stringify(err)
          });
        });
    }
  }

  componentWillUnmount() {
    if (this.props.settings.timeout && !this.timeOutDisabled) {
      clearInterval();
      this.props.showTimeOutDialog(false);
    }
  }


  startInterval() {
    this.interval = window.setInterval(() => {
      if (this.elapseSec === 0) {
        this.reset();
        // If photoswipe is currently open, mark that it should be restored after timeout dialog is cleared
        this.props.setPhotoSwipeHidden(true);
        this.props.showTimeOutDialog(true);
      } else {
        if (this.props.timeOutDialogVisible === false) {
          this.elapseSec -= 1;
        }
      }
    }, 1000);

    window.addEventListener("mousemove", () => this.reset());
    window.addEventListener("scroll", () => this.reset());
    window.addEventListener("keydown", () => this.reset());
  }

  clearInterval() {
    window.clearInterval(this.interval);
    this.cleanUp();
  }

  cleanUp() {
    window.removeEventListener("mousemove", () => this.reset());
    window.removeEventListener("scroll", () => this.reset());
    window.removeEventListener("keydown", () => this.reset());

    document.body.style.overflowY = 'scroll';
  }

  reset() {
    // prevent reset if modal is open, otherwise the displayed countdown would reset
    if (!this.props.timeOutDialogVisible) {
      this.elapseSec = this.props.settings.timeout;
    }
  }

  getJobs(token) {
    return KioskService.getJobs(token, this.since)
      .then(jobs => {
        this.storeResource('jobs', jobs);

        return Promise.resolve();
      });
  }

  async loadJobs() {
    const { settings: { token } } = this.props;
    const { jobs, jobMoments } = this.store;

    if (!jobs.allIds.length) {
      return;
    }

    let threads = 10;
    let tasks = [];
    let results = {}; // {<job id>: {<page>: momentIds, ...}}
    let perPage = 1000;
    let total = jobs.allIds.reduce((t, id) => t += jobs.byId[id].moment_count, 0);

    this.updateLog('Loaded jobs.');
    this.appendLog(`Loading moments 0/${total}...`);

    for (let id of jobs.allIds) {
      let job = jobs.byId[id];
      let pages = Math.ceil(job.moment_count / perPage);

      results[job.id.toString()] = {};

      for (let i = 0; i < pages; i++) {
        let page = i + 1;

        let task = async () => {
          let moments = await KioskService.getMoments(token, job.id, page, perPage, this.since);
          this.storeResource('moments', moments);

          let momentIds = moments.map(m => m.id);
          results[job.id.toString()][page.toString()] = momentIds;

          let log = this.getLog();
          let count = parseInt(log.match(/(\d+)\/\d+/)[1]);
          let nextCount = count + perPage;
          nextCount = nextCount > total ? total : nextCount;
          this.updateLog(`Loading moments ${nextCount}/${total}...`);
        }


        tasks.push(task);
      }
    }

    await juggle(threads, tasks);

    this.updateLog('Loaded moments.');

    for (let jobId in results) {
      jobMoments[jobId] = [];

      let pages = Object.keys(results[jobId])
        .map(page => parseInt(page))
        .sort();

      for (let page of pages) {
        let momentIds = results[jobId][page];
        jobMoments[jobId] = jobMoments[jobId].concat(momentIds);
      }
    }
  }

  onMomentAvailable(moment) {
    const { user, addMoment } = this.props;

    // kiosk_job_id only set for tagged images.  Falling back to job_id when not available
    if (moment.kiosk_job_id || moment.job_id) {
      let jobId = moment.kiosk_job_id || moment.job_id
      jobId = jobId.toString();

      if (this.store.jobMoments[jobId]) {
        this.storeResource("moments", moment);
        this.store.jobMoments[jobId].push(moment.id);
      }
    }

    if (user && moment && moment.story_ids) {
      if (moment.story_ids.includes(user.story_id)) {
        addMoment(moment).then(() => {

          // Need to update purchase calcs to reflect latest count in gallery
          // Using latest count from catalogs when doing so just in case
          let maxQuantity = this.store.moments.allIds.length;

          // Pull moments from Redux if not stored in window
          if (maxQuantity == 0) {
            maxQuantity = this.props.moments.allIds.length;
          }
          this.props.getPurchaseCalculations(user.story_id, maxQuantity)
        })
      }
    }
  }

  storeResource(resource, payload) {
    if (Array.isArray(payload)) {
      payload.forEach(record => {
        this.store[resource].byId[record.id] = record;
        this.store[resource].allIds.push(record.id);
      });
    } else {
      this.store[resource].byId[payload.id] = payload;

      if (this.store[resource].allIds.indexOf(payload.id) === -1) {
        this.store[resource].allIds.push(payload.id);
      }
    }

    this.store[resource].allIds = this.store[resource].allIds.filter((value, index, self) => {
      return self.indexOf(value) === index;
    })
      .sort((a, b) => a - b);
  }

  getLog() {
    let { logs } = this.state
    return logs[logs.length - 1];
  }

  updateLog(log) {
    let { logs } = this.state
    logs[logs.length - 1] = log;
    this.setState({ logs });
  }

  appendLog(log) {
    let { logs } = this.state
    logs.push(log);
    this.setState({ logs });
  }

  render() {
    const { classes } = this.props;

    if (this.state.kioskNotFound) {
      return (<Redirect to="/" />);
    }

    if (this.state.loading) {
      let error = null;
      if (this.state.error) {
        error = (
          <p className={classes.text} style={{ color: 'red' }}>
            {this.state.error}
          </p>
        );
      }

      return (
        <div style={{ float: 'left', padding: '2rem' }}>
          {this.state.logs.map(log => (
            <p className={classes.text}>
              {log}
            </p>
          ))}
          {error}
        </div>
      );
    }

    return React.Children.map(this.props.children, child => {
      return React.cloneElement(child, {
        store: this.store
      });
    })
  }
}

const mapStateToProps = state => ({
  loaded: state.kioskReducer.loaded,
  user: state.kioskReducer.user,
  settings: state.kioskReducer.settings,
  moments: state.cartReducer.moments,
  timeOutDialogVisible: state.kioskReducer.timeOutDialogVisible
});

export default connect(mapStateToProps, { getKioskPrintSizes, getKioskSettings, setKioskLoaded, setUppercaseLabels, selectLocale, addMoment, getPurchaseCalculations, getDefaultStripeReader, setPhotoSwipeHidden, showTimeOutDialog })(withRouter(withStyles(styles)(LoadKiosk)));
